import { useState, useEffect } from "react";

const analysisItems = [
  { label: "用气规律", value: "工作日用气集中在07:00-09:00及18:00-21:00，符合典型居民作息模式", icon: "ri-time-line", color: "text-teal-600" },
  { label: "消费趋势", value: "近6个月用气量呈稳定增长趋势，月均增幅约3.2%，预测下月用量约 42.8m³", icon: "ri-line-chart-line", color: "text-green-600" },
  { label: "异常检测", value: "2026-03-15 检测到用气量突增，较日均高出320%，已自动触发异常告警并通知安检员", icon: "ri-alert-line", color: "text-amber-600" },
  { label: "安全评估", value: "综合设备年龄、用气行为、安检记录，当前安全评分 92分，处于优良水平", icon: "ri-shield-check-line", color: "text-orange-500" },
];

const radarData = [
  { label: "用气规律", score: 88 },
  { label: "缴费信用", score: 95 },
  { label: "设备状态", score: 82 },
  { label: "安全合规", score: 92 },
  { label: "服务满意", score: 78 },
];

export default function AIAnalysisPanel() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"insight" | "radar">("insight");

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(t);
  }, []);

  const cx = 100, cy = 100, r = 70;
  const angles = radarData.map((_, i) => (i * 2 * Math.PI) / radarData.length - Math.PI / 2);
  const points = radarData.map((d, i) => ({
    x: cx + (r * d.score / 100) * Math.cos(angles[i]),
    y: cy + (r * d.score / 100) * Math.sin(angles[i]),
  }));
  const gridPoints = (scale: number) => radarData.map((_, i) => ({
    x: cx + r * scale * Math.cos(angles[i]),
    y: cy + r * scale * Math.sin(angles[i]),
  }));
  const toPath = (pts: { x: number; y: number }[]) =>
    pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ") + " Z";

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#E8531A]/10">
            <i className="ri-brain-line text-[#E8531A] text-sm"></i>
          </div>
          <span className="text-sm font-semibold text-gray-800">AI 用户画像分析</span>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {(["insight", "radar"] as const).map(t => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${activeTab === t ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
            >
              {t === "insight" ? "智能洞察" : "雷达评分"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32 gap-2">
          <i className="ri-loader-4-line animate-spin text-[#E8531A] text-xl"></i>
          <span className="text-sm text-gray-400">AI 分析中...</span>
        </div>
      ) : activeTab === "insight" ? (
        <div className="space-y-2">
          {analysisItems.map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${item.icon} ${item.color} text-sm`}></i>
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-700">{item.label}：</span>
                <span className="text-xs text-gray-600">{item.value}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <svg viewBox="0 0 200 200" className="w-44 h-44 flex-shrink-0">
            {[0.25, 0.5, 0.75, 1].map(scale => (
              <polygon
                key={scale}
                points={gridPoints(scale).map(p => `${p.x},${p.y}`).join(" ")}
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="1"
              />
            ))}
            {angles.map((a, i) => (
              <line key={i} x1={cx} y1={cy} x2={cx + r * Math.cos(a)} y2={cy + r * Math.sin(a)} stroke="#e5e7eb" strokeWidth="1" />
            ))}
            <polygon
              points={points.map(p => `${p.x},${p.y}`).join(" ")}
              fill="rgba(232,83,26,0.15)"
              stroke="#E8531A"
              strokeWidth="1.5"
            />
            {points.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="3" fill="#E8531A" />
            ))}
            {radarData.map((d, i) => {
              const lx = cx + (r + 16) * Math.cos(angles[i]);
              const ly = cy + (r + 16) * Math.sin(angles[i]);
              return (
                <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="#6b7280">
                  {d.label}
                </text>
              );
            })}
          </svg>
          <div className="flex-1 space-y-2">
            {radarData.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-16 flex-shrink-0">{d.label}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                  <div className="bg-[#E8531A] h-1.5 rounded-full" style={{ width: `${d.score}%` }} />
                </div>
                <span className="text-xs font-bold text-gray-700 w-8 text-right">{d.score}</span>
              </div>
            ))}
            <div className="mt-3 p-2 bg-orange-50 rounded-lg">
              <p className="text-xs text-[#E8531A] font-medium">综合评分：90分</p>
              <p className="text-xs text-gray-500 mt-0.5">优质用户，建议优先提供增值服务</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
